const DEFAULT_MAX_PRICE = 10_000_000;

function profitAtCents(profitAtPrice, priceCents) {
  return Number(profitAtPrice(priceCents / 100));
}

function reachedTarget(profitAtPrice, priceCents, targetProfit) {
  const profit = profitAtCents(profitAtPrice, priceCents);
  return Number.isFinite(profit) && profit >= targetProfit;
}

function firstMatchInMonotonicRange(
  profitAtPrice,
  targetProfit,
  startCents,
  endCents,
) {
  if (reachedTarget(profitAtPrice, startCents, targetProfit)) return startCents;
  if (!reachedTarget(profitAtPrice, endCents, targetProfit)) return null;

  let left = startCents;
  let right = endCents;
  while (left + 1 < right) {
    const middle = Math.floor((left + right) / 2);
    if (reachedTarget(profitAtPrice, middle, targetProfit)) right = middle;
    else left = middle;
  }
  return right;
}

/**
 * Finds the minimum seller price that reaches the requested profit after ads.
 *
 * Marketplace formulas are monotonic between known price tariff boundaries.
 * Searching each interval independently preserves discontinuities at those
 * boundaries while requiring only logarithmic calculator evaluations.
 */
export function findMinimumSellerPrice(
  profitAtPrice,
  targetProfit = 0,
  {
    breakpoints = [],
    maxPrice = DEFAULT_MAX_PRICE,
  } = {},
) {
  if (typeof profitAtPrice !== "function") return null;
  const target = Number(targetProfit);
  const maximum = Number(maxPrice);
  if (!Number.isFinite(target) || !Number.isFinite(maximum) || maximum < 0) return null;

  const maxCents = Math.floor(maximum * 100);
  const breakpointCents = [...new Set(
    breakpoints
      .map(Number)
      .filter((value) => Number.isFinite(value) && value >= 0 && value < maximum)
      .map((value) => Math.floor(value * 100)),
  )].sort((left, right) => left - right);

  let startCents = 0;
  for (const endCents of [...breakpointCents, maxCents]) {
    if (startCents <= endCents) {
      const match = firstMatchInMonotonicRange(
        profitAtPrice,
        target,
        startCents,
        endCents,
      );
      if (match != null) return match / 100;
    }
    // A tariff declared as "up to N RUB" changes at the next valid kopeck.
    startCents = endCents + 1;
  }
  return null;
}
